import React, { useState, useEffect } from 'react';
import { YouTubeConnect } from '../components/YouTubeConnect';
import { VideoUploadForm } from '../components/VideoUploadForm';
import { ScheduledPostsList } from '../components/ScheduledPostsList';
import './YouTubePage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

export const YouTubePage = ({ session, profile }) => {
    // MOCK DATA FOR DEV AUTH BYPASS
    const devAuth = new URLSearchParams(window.location.search).get('dev_auth') === '1';

    let effectiveSession = session;

    if (devAuth) {
        effectiveSession = { access_token: 'mock-token' };
    }

    const [isConnected, setIsConnected] = useState(false);
    const [channelName, setChannelName] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [previewVideo, setPreviewVideo] = useState(null); // { url, type: 'video/mp4' }

    // Check connection status on mount
    useEffect(() => {
        const checkStatus = async () => {
            if (!effectiveSession?.access_token) return;

            try {
                const res = await fetch(`${API_BASE}/api/youtube/status`, {
                    headers: { Authorization: `Bearer ${effectiveSession.access_token}` }
                });
                const data = await res.json();
                if (res.ok) {
                    setIsConnected(data.connected);
                    setChannelName(data.channel || '');
                }
            } catch (err) {
                console.error('Failed to check YouTube status:', err);
            }
        };

        checkStatus();
    }, [effectiveSession]);

    const handleConnectionChange = (connected, channel) => {
        setIsConnected(connected);
        if (channel) setChannelName(channel);
    };

    const handleUploadComplete = (response) => {
        // Refresh scheduled posts list
        setRefreshKey(prev => prev + 1);
    };

    const handleVideoSelect = (video) => {
        // video: { url: string, type: string } | null
        setPreviewVideo(video);
    };

    if (!effectiveSession && !devAuth) {
        return <div className="page-container">Please log in to use YouTube integration.</div>;
    }

    return (
        <div className="youtube-page">
            <div className="youtube-page-header">
                <h1>YouTube Studio</h1>
                <p className="subtitle">Connect your channel to upload videos directly or schedule them for later.</p>
            </div>

            <div className="youtube-layout">
                {/* Left Column - Upload Form */}
                <div className="youtube-left-column">
                    <div className="glass-card">
                        <div className="card-header">
                            <h2>Upload Video</h2>
                            {!isConnected && (
                                <span className="connection-required-badge">Connection Required</span>
                            )}
                        </div>
                        <VideoUploadForm
                            session={effectiveSession}
                            isConnected={isConnected}
                            onUploadComplete={handleUploadComplete}
                            onVideoSelect={handleVideoSelect}
                        />
                    </div>
                </div>

                {/* Right Column - Connect & Preview */}
                <div className="youtube-right-column">
                    {/* Connect Section */}
                    <div className="glass-card connect-card-wrapper">
                        <YouTubeConnect
                            session={effectiveSession}
                            onConnectionChange={handleConnectionChange}
                        />
                    </div>

                    {/* Video Preview Card */}
                    <div className="glass-card preview-card">
                        <div className="card-header">
                            <h2>Video Preview</h2>
                        </div>
                        <div className="preview-content">
                            {previewVideo ? (
                                <div className="video-player-wrapper">
                                    <video
                                        key={previewVideo.url} // Force reload on change
                                        controls
                                        src={previewVideo.url}
                                        className="preview-player"
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                            ) : (
                                <div className="preview-placeholder">
                                    <p>Select a video to preview</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Scheduled Posts (if connected) */}
                    {isConnected && (
                        <div className="glass-card scheduled-posts-card">
                            <ScheduledPostsList
                                key={refreshKey}
                                session={effectiveSession}
                                isConnected={isConnected}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

};
